/**
 * The meditation's languages (LanguagePicker spec § Anatomy) — not the site-routing languages of
 * `lib/hreflang.ts`. Endonyms are names, not drafted copy, so they live here beside the ids.
 */

export type LanguageOption = {
    code: string
    flag: string
    endonym: string
    bcp47: string
}

export const LANGUAGES: LanguageOption[] = [
    {code: 'en-uk', flag: '\u{1F1EC}\u{1F1E7}', endonym: 'English (UK)', bcp47: 'en-GB'},
    {code: 'en-us', flag: '\u{1F1FA}\u{1F1F8}', endonym: 'English (US)', bcp47: 'en-US'},
    {code: 'fr', flag: '\u{1F1EB}\u{1F1F7}', endonym: 'Français', bcp47: 'fr'},
    {code: 'es', flag: '\u{1F1EA}\u{1F1F8}', endonym: 'Español', bcp47: 'es'},
    {code: 'de', flag: '\u{1F1E9}\u{1F1EA}', endonym: 'Deutsch', bcp47: 'de'},
    {code: 'it', flag: '\u{1F1EE}\u{1F1F9}', endonym: 'Italiano', bcp47: 'it'},
    {code: 'pt', flag: '\u{1F1E7}\u{1F1F7}', endonym: 'Português', bcp47: 'pt-BR'},
    {code: 'nl', flag: '\u{1F1F3}\u{1F1F1}', endonym: 'Nederlands', bcp47: 'nl'},
    {code: 'pl', flag: '\u{1F1F5}\u{1F1F1}', endonym: 'Polski', bcp47: 'pl'},
    {code: 'sv', flag: '\u{1F1F8}\u{1F1EA}', endonym: 'Svenska', bcp47: 'sv'},
    {code: 'ru', flag: '\u{1F1F7}\u{1F1FA}', endonym: 'Русский', bcp47: 'ru'},
    {code: 'tr', flag: '\u{1F1F9}\u{1F1F7}', endonym: 'Türkçe', bcp47: 'tr'},
    {code: 'ar', flag: '\u{1F1F8}\u{1F1E6}', endonym: 'العربية', bcp47: 'ar'},
    {code: 'hi', flag: '\u{1F1EE}\u{1F1F3}', endonym: 'हिन्दी', bcp47: 'hi'},
    {code: 'zh', flag: '\u{1F1E8}\u{1F1F3}', endonym: '中文', bcp47: 'zh'},
    {code: 'ja', flag: '\u{1F1EF}\u{1F1F5}', endonym: '日本語', bcp47: 'ja'},
    {code: 'ko', flag: '\u{1F1F0}\u{1F1F7}', endonym: '한국어', bcp47: 'ko'},
]

export function findLanguage(code: string | null): LanguageOption | null {
    return LANGUAGES.find((l) => l.code === code) ?? null
}

/** The first match of `navigator.languages` (LanguagePicker spec § Behaviour): exact tag, then the
 *  primary subtag; English other than en-GB is English (US). */
export function preselectLanguage(navigatorLanguages: readonly string[]): string | null {
    for (const raw of navigatorLanguages) {
        const tag = raw.toLowerCase()
        const primary = tag.split('-')[0]

        const exact = LANGUAGES.find((l) => l.bcp47.toLowerCase() === tag)
        if (exact) return exact.code

        if (primary === 'en') return tag === 'en-gb' ? 'en-uk' : 'en-us'

        const byPrimary = LANGUAGES.find((l) => l.bcp47.toLowerCase().split('-')[0] === primary)
        if (byPrimary) return byPrimary.code
    }
    return null
}

/** A language's name in itself, from its primary subtag (`en`, `fr-FR`): `English`, not `English (UK)`. */
export function languageName(code: string): string {
    const primary = code.toLowerCase().split('-')[0]
    const option = LANGUAGES.find((l) => l.code === primary || l.code.startsWith(`${primary}-`))
    return (option?.endonym ?? primary).replace(/\s*\(.*\)$/, '')
}
